/**
 * Per-comedian show-page visibility.
 *
 * `show_comedian_pairings.hidden_from_comedian_page` is scoped per
 * (show_id, comedian_id) — a show tagged to multiple comedians (e.g. a
 * venue-created festival lineup) can be hidden for one comedian and visible
 * for another. Any consumer that lists "this comedian's shows" must match the
 * pairing for THAT comedian specifically — never assume the first pairing in
 * `show_comedians` (it is ordered by `ordering`, not by the requested
 * comedian).
 *
 * See `docs/specs/show-visibility.md` for the full data model (write paths,
 * the independent venue-page visibility axis, and the consumer audit).
 */
export function isShowVisibleOnComedianPage(
  show: {
    show_comedians?: { id: string; hidden_from_comedian_page: boolean }[];
  },
  comedianId: string
): boolean {
  const pairing = show.show_comedians?.find((sc) => sc.id === comedianId);
  // Fail closed: no pairing for this comedian reads as hidden, matching
  // TaggedRunCard's "missing pairing reads as hidden" convention.
  return pairing !== undefined && !pairing.hidden_from_comedian_page;
}

/**
 * Per-venue-page visibility, seen from the comedian-page reader.
 *
 * The venue that created a show can hide it on its venue page
 * (`venue_pages_shows.show_visibility_status = 'hidden'`). Because the venue is
 * the show's creator, that hide ripples to every comedian's page: a
 * venue-hidden show must not appear on a tagged comedian's page even when the
 * comedian's own pairing is visible (PUN-5017). A show hidden on ANY of its
 * venue pages counts as venue-hidden; `pending_review` does not (only an
 * explicit `'hidden'` ripples, matching the venue-page reader's own convention).
 */
export function isShowVenueHidden(show: {
  venue_pages?: { show_visibility_status?: string | null }[];
}): boolean {
  return (
    show.venue_pages?.some((vp) => vp.show_visibility_status === "hidden") ??
    false
  );
}

/**
 * Per-venue-page visibility, seen from that venue page's own reader: true when
 * THIS venue page has set the show to `'hidden'`. A hide on a different venue
 * page does not count here; `isShowVenueHidden` is the any-page ripple.
 */
export function isShowHiddenOnVenuePage(
  show: {
    venue_pages?: { id: string; show_visibility_status?: string | null }[];
  },
  venuePageId: string
): boolean {
  return (
    show.venue_pages?.some(
      (vp) => vp.id === venuePageId && vp.show_visibility_status === "hidden"
    ) ?? false
  );
}

/**
 * The rule for listing a show on a comedian's page (and for comedian-reachable
 * checkout): visible only when it is visible on BOTH axes — the comedian's own
 * pairing AND the venue's page. See `docs/specs/show-visibility.md`.
 */
export function isShowVisibleOnComedianPageListing(
  show: {
    show_comedians?: { id: string; hidden_from_comedian_page: boolean }[];
    venue_pages?: { show_visibility_status?: string | null }[];
  },
  comedianId: string
): boolean {
  return (
    isShowVisibleOnComedianPage(show, comedianId) && !isShowVenueHidden(show)
  );
}
